import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import MainContainer from "../../components/MainContainer";

import {
  selectSelectedCollection,
  selectEnableBlendTrainModelPending,
  selectBlendCollectionForTrainModelPending,
  selectSelectedTrainModel,
  selectUpdateBlendTrainModelPending,
  setSelectedCollection,
} from "../../store/slices/model/slice";

import {
  BackButton,
  ModelTitle,
  SkeletonLoader,
  ModelDescription,
  ModelColumn,
  ModelRow,
  TagsContainer,
  Tag,
  TagText,
  ModelTitleRow,
  TextInput,
  SmallRow,
  Label,
  Value,
  TextArea,
  ThinText,
  SelectContainer,
  Column,
  InputContainer,
  FormInput,
  LineHeader,
  Line,
  LineText,
  CollectionRow,
  Buttons,
} from "./EditTrainingModelPage.styles";

import { ReactComponent as ChevronLeft } from "../../assets/icons/chevronLeft.svg";
import { ReactComponent as MagicStickSvg } from "../../assets/icons/magicStick.svg";
import { ReactComponent as EditIcon } from "../../assets/icons/edit.svg";
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import { ReactComponent as SubmitIcon } from "../../assets/icons/check01.svg";
import { ReactComponent as RatingStarIcon } from "../../assets/icons/whiteStarOutline.svg";
import { ReactComponent as ShareIcon } from "../../assets/icons/share.svg";
import { ReactComponent as UsersIcon } from "../../assets/icons/users-02.svg";

import { selectLoggedIn } from "../../store/slices/auth/slice";
import Button from "../../components/utils/Button/Button";
import { toast } from "../../components/modern-ui/sonner";

import EditTagsModal from "../../components/EditTagsModal/EditTagsModal";
import GenerateCollectionModal from "../../components/GenerateCollectionModal/GenerateCollectionModal";
import PublishOnFapifyModal from "../../components/PublishOnFapifyModal/PublishOnFapifyModal";
import {
  enableBlendTrainModel,
  getTrainModel,
  selectBlendCollectionForTrainModel,
  updateBlendTrainModel,
} from "../../store/slices/model/asyncThunks";
import modelService from "../../api/services/model";
import { COLLECTION_STATUS, TAGS } from "../../utils/constants";
import { formatDate, isImageOutput } from "../../utils/utils";
import { selectUser } from "../../store/slices/user/slice";
import LottieIcon from "../../components/LottieIcon/LottieIcon";
import radioButtonAnimation from "../../assets/animations/radioButton.json";
import EditImageModal from "../../components/EditImageModal/EditImageModal";
import GroupModal from "../../components/GroupModal/GroupModal";
import CollectionItem from "../../components/utils/CollectionItem/CollectionItem";

const EditTrainingModelPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();

  const [showEditTagsModal, setShowEditTagsModal] = useState(false);
  const [isGenerateCollectionModalOpen, setIsGenerateCollectionModalOpen] =
    useState(false);
  const selectedCollection = useSelector(selectSelectedCollection);
  const [isEditImageModalOpen, setIsEditImageModalOpen] = useState(false);
  const [isPublishOnFapifyModalOpen, setIsPublishOnFapifyModalOpen] =
    useState(false);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);

  const [templateName, setTemplateName] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [editTemplateName, setEditTemplateName] = useState(false);
  const [description, setDescription] = useState("");
  const [editDescription, setEditDescription] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [blendModelName, setBlendModelName] = useState("");
  const [blendPrompt, setBlendPrompt] = useState("");
  const [savedBlendModelName, setSavedBlendModelName] = useState("");
  const [savedBlendPrompt, setSavedBlendPrompt] = useState("");
  const [selectedCoverId, setSelectedCoverId] = useState(null);

  const selectedTrainModel = useSelector(selectSelectedTrainModel);
  const enableBlendLoading = useSelector(selectEnableBlendTrainModelPending);
  const publishBlendLoading = useSelector(
    selectBlendCollectionForTrainModelPending,
  );
  const updateBlendLoading = useSelector(selectUpdateBlendTrainModelPending);
  const isLoggedIn = useSelector(selectLoggedIn);

  const trainModelId = params.modelId;

  const user = useSelector(selectUser);
  const isVideoTraining = selectedTrainModel?.data?.type !== "image";

  const isBlendEnabled = !!selectedTrainModel?.data?.isBlendEnabled;
  const hasCoupleKeyword = selectedTags.some(
    (tag) => String(tag).trim().toLowerCase() === "couple",
  );

  const isBlendPublished = !!selectedTrainModel?.data?.selectedBlendCollection;
  const shouldShowBlendMode = isBlendEnabled && !isBlendPublished;

  const blendCollections = selectedTrainModel?.data?.blendCollections;
  const trimmedBlendModelName = blendModelName.trim();
  const trimmedBlendPrompt = blendPrompt.trim();
  const isBlendReadyToCreate =
    !!trimmedBlendModelName &&
    !!trimmedBlendPrompt &&
    trimmedBlendModelName === savedBlendModelName &&
    trimmedBlendPrompt === savedBlendPrompt;

  useEffect(() => {
    if (selectedTrainModel && selectedTrainModel.data) {
      setTemplateName(selectedTrainModel.data.templateName ?? "Template name");
      setSelectedTags(selectedTrainModel?.data?.keywords ?? []);
      setDescription(selectedTrainModel.data.description || "");
      setIsChecked(selectedTrainModel.data?.addToVipCategory ?? false);
    }
  }, [selectedTrainModel]);

  useEffect(() => {
    if (!selectedTrainModel?.data?._id) return;

    const defaultBlendModelName =
      selectedTrainModel?.data?.templateName?.trim() ||
      selectedTrainModel?.data?.name?.trim() ||
      "";

    setBlendModelName(
      selectedTrainModel?.data?.blendModel?.displayName ??
        (defaultBlendModelName ? `${defaultBlendModelName} Blend` : ""),
    );
    setBlendPrompt(selectedTrainModel?.data?.groupPrompt ?? "");
    setSavedBlendModelName(
      selectedTrainModel?.data?.blendModel?.displayName?.trim() ?? "",
    );
    setSavedBlendPrompt(selectedTrainModel?.data?.groupPrompt?.trim() ?? "");
  }, [
    selectedTrainModel?.data?._id,
    selectedTrainModel?.data?.blendModel?.displayName,
    selectedTrainModel?.data?.groupPrompt,
    selectedTrainModel?.data?.name,
    selectedTrainModel?.data?.templateName,
  ]);

  useEffect(() => {
    if (isLoggedIn && trainModelId) {
      dispatch(getTrainModel(trainModelId));
    }
  }, [trainModelId, isLoggedIn, dispatch]);

  const getTrainModelFromBlendResponse = (payload) =>
    payload?.data?.trainModel ?? payload?.trainModel ?? payload?.data ?? null;

  const handleBack = () => {
    navigate("/train");
  };

  const selectTagsAction = async (data) => {
    try {
      await modelService.selectTagsForTrainModel(data);
      toast.success("Template edited successfully.");
    } catch (err) {
      console.log(err);
      toast.error("Failed to update template");
    }
  };

  const handleGenerateAgain = () => {
    const modelObject = {
      ...selectedTrainModel.data?.selectedCollection,
      regenerate: false,
      collectionId: selectedTrainModel.data?.selectedCollection._id,
    };

    dispatch(setSelectedCollection(modelObject));
    if (isImageOutput(modelObject.modelType ?? modelObject.modelName)) {
      setIsEditImageModalOpen(true);
    } else {
      setIsGenerateCollectionModalOpen(true);
    }
  };

  const handleEnableBlend = async () => {
    if (enableBlendLoading || !selectedTrainModel?.data?._id) return false;
    try {
      const response = await dispatch(
        enableBlendTrainModel({
          trainModelId: selectedTrainModel.data._id,
        }),
      ).unwrap();
      toast.success("Blend mode enabled");
      return getTrainModelFromBlendResponse(response) ?? true;
    } catch (err) {
      toast.error(err || "Failed to enable blend mode");
      return false;
    }
  };

  const handleCreateBlend = async () => {
    const selectedBlendCollection =
      selectedTrainModel?.data?.selectedBlendCollection;
    const firstBlendCollection = selectedTrainModel?.data?.blendCollections[0];

    const hasSelectedBlendCollectionData =
      !!selectedBlendCollection &&
      typeof selectedBlendCollection === "object" &&
      !Array.isArray(selectedBlendCollection) &&
      !!selectedBlendCollection?._id &&
      !!(
        selectedBlendCollection?.input ||
        selectedBlendCollection?.output ||
        selectedBlendCollection?.model ||
        selectedBlendCollection?.modelId
      );
    const cover =
      (hasSelectedBlendCollectionData ? selectedBlendCollection : null) ||
      (firstBlendCollection?.status === COLLECTION_STATUS.COMPLETED
        ? firstBlendCollection
        : null);

    let blendModelId = selectedTrainModel?.data?.blendModel?._id;
    if (!trimmedBlendModelName) {
      toast.error("Please enter a blend template name");
      return;
    }

    if (!trimmedBlendPrompt) {
      toast.error("Please enter a blend prompt");
      return;
    }

    if (!isBlendReadyToCreate) {
      toast.error("Please save the blend template name and prompt first");
      return;
    }

    if (!isBlendEnabled) {
      const updatedTrainModel = await handleEnableBlend();
      if (!updatedTrainModel) return;

      blendModelId = updatedTrainModel?.blendModel?._id ?? blendModelId;
    }

    console.log(cover);
    if (!cover) {
      const { input, ...selectedCollectionWithoutMedia } =
        selectedTrainModel?.data?.selectedCollection || {};

      dispatch(
        setSelectedCollection({
          ...selectedCollectionWithoutMedia,
          model: blendModelId,
          regenerate: false,
          input: {},
          hasBlendCollection: !!cover,
          trainModel: trainModelId,
          name: trimmedBlendModelName,
        }),
      );
    } else {
      dispatch(
        setSelectedCollection({
          ...cover,
          regenerate: false,
          collectionId: cover._id,
        }),
      );
    }
    setIsGroupModalOpen(true);
  };

  const handleCreateBlendClick = () => {
    if (updateBlendLoading) {
      toast.error("Please wait for blend settings to finish saving");
      return;
    }

    if (!trimmedBlendModelName || !trimmedBlendPrompt) {
      toast.error("Enter and save the blend template name and prompt first");
      return;
    }

    if (!isBlendReadyToCreate) {
      toast.error("Save the blend template name and prompt before creating");
      return;
    }

    handleCreateBlend();
  };

  const handleSaveBlendSettings = async () => {
    if (!selectedTrainModel?.data?._id || updateBlendLoading) return;

    if (!trimmedBlendModelName && !trimmedBlendPrompt) {
      toast.error("Blend template name and blend prompt is required");
      return;
    }

    try {
      await dispatch(
        updateBlendTrainModel({
          trainModelId: selectedTrainModel.data._id,
          ...(trimmedBlendModelName && { name: trimmedBlendModelName }),
          ...(trimmedBlendPrompt && { groupPrompt: trimmedBlendPrompt }),
        }),
      ).unwrap();
      setSavedBlendModelName(trimmedBlendModelName);
      setSavedBlendPrompt(trimmedBlendPrompt);
      toast.success("Blend settings saved");
    } catch (err) {
      toast.error(err || "Failed to save blend settings");
    }
  };

  const handleSaveDescription = async () => {
    // if (!description || description.trim().length === 0) {
    //   toast.error("Usage instructions cannot be empty");
    //   return;
    // }

    // if (description.trim().length < 20) {
    //   toast.error("Usage instructions should be at least 20 characters");
    //   return;
    // }

    setEditDescription(false);
    await selectTagsAction({
      ...(description && description.length && { description }),
      trainModelId: selectedTrainModel.data._id,
    });
  };

  const handleCancelDescription = () => {
    setEditDescription(false);
    setDescription(selectedTrainModel.data.description || "");
  };

  const handleSelectCover = (updater) => {
    setSelectedCoverId((prev) => {
      const prevArr = prev ? [prev] : [];
      const nextArr =
        typeof updater === "function" ? updater(prevArr) : updater;
      if (!nextArr || nextArr.length === 0) return null;
      return nextArr[nextArr.length - 1];
    });
  };

  const handlePublishBlend = async () => {
    if (!selectedCoverId) return;

    try {
      await dispatch(
        selectBlendCollectionForTrainModel({
          trainModelId,
          collectionId: selectedCoverId,
        }),
      ).unwrap();

      setSelectedCoverId(null);
      toast.success("Blend collection published");
    } catch (err) {
      toast.error(err || "Failed to publish blend collection");
    }
  };

  // Show loading state
  if (selectedTrainModel.loading) {
    return (
      <MainContainer>
        <BackButton onClick={handleBack}>
          <ChevronLeft />
          Back to Train
        </BackButton>

        <ModelRow>
          <ModelColumn>
            <SkeletonLoader $height="28px" $width="240px" $radius="4px" />
            <SkeletonLoader $height="44px" $width="280px" $radius="12px" />
            <SkeletonLoader $height="14px" $width="120px" $radius="4px" />
            <SkeletonLoader $height="80px" $width="100%" $radius="12px" />
            <SkeletonLoader $height="14px" $width="160px" $radius="4px" />
            <TagsContainer>
              <SkeletonLoader $height="24px" $width="64px" $radius="8px" />
              <SkeletonLoader $height="24px" $width="80px" $radius="8px" />
              <SkeletonLoader $height="24px" $width="56px" $radius="8px" />
              <SkeletonLoader $height="24px" $width="72px" $radius="8px" />
            </TagsContainer>
          </ModelColumn>
          <Column style={{ gap: 9 }}>
            <SkeletonLoader $height="44px" $width="185px" $radius="100px" />
          </Column>
        </ModelRow>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <BackButton onClick={handleBack}>
        <ChevronLeft />
        Back to Train
      </BackButton>

      <ModelRow>
        <ModelColumn>
          {editTemplateName ? (
            <ModelTitleRow>
              <TextInput
                autoFocus
                value={templateName}
                onChange={(e) => {
                  setTemplateName(e.target.value);
                }}
              />
              <SubmitIcon
                width={18}
                height={18}
                style={{ cursor: "pointer" }}
                onClick={async () => {
                  setEditTemplateName(false);
                  await selectTagsAction({
                    name: templateName,
                    trainModelId: selectedTrainModel.data._id,
                  });
                }}
              />
              <CloseIcon
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setEditTemplateName(false);
                  setTemplateName(
                    selectedTrainModel.data.templateName ?? "Template name",
                  );
                }}
              />
            </ModelTitleRow>
          ) : (
            <ModelTitleRow>
              <ModelTitle>{templateName}</ModelTitle>
              <EditIcon
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setEditTemplateName(true);
                }}
              />
            </ModelTitleRow>
          )}

          {user?.isVipCreator && selectedTrainModel?.data?.type !== "image" && (
            <SelectContainer
              onClick={async () => {
                await selectTagsAction({
                  addToVipCategory: !isChecked,
                  trainModelId: selectedTrainModel.data._id,
                });

                setIsChecked((prev) => !prev);
              }}
            >
              <LottieIcon
                animationData={radioButtonAnimation}
                speed={2}
                trigger="toggle-reverse"
                isActive={isChecked}
                size={22}
                strokeColor={!isChecked ? "#2877d9" : "#ffffff"}
              />
              Include this template in VIP Section
            </SelectContainer>
          )}

          {!!selectedTrainModel?.data?.model?.publishedAt && (
            <SmallRow>
              <Label>Publication Date:</Label>
              <Value>
                {formatDate(
                  new Date(selectedTrainModel?.data?.model?.publishedAt),
                  true,
                )}
              </Value>
            </SmallRow>
          )}
          {!!selectedTrainModel?.data?.model?.rating?.score && (
            <SmallRow>
              <Label>Rating:</Label>
              <SmallRow>
                <RatingStarIcon width={14} height={14} />
                <Value>
                  {selectedTrainModel?.data?.model?.rating?.score?.toFixed(1)} /
                  10
                </Value>
              </SmallRow>
            </SmallRow>
          )}

          {/* Usage Instructions Section */}
          {editDescription ? (
            <ModelColumn style={{ gap: "8px", flex: "unset" }}>
              <ModelTitleRow>
                <ModelDescription>Usage Instructions</ModelDescription>
                <SubmitIcon
                  width={18}
                  height={18}
                  style={{ cursor: "pointer" }}
                  onClick={handleSaveDescription}
                />
                <CloseIcon
                  style={{ cursor: "pointer" }}
                  onClick={handleCancelDescription}
                />
              </ModelTitleRow>
              <TextArea
                autoFocus
                placeholder="Explain how others should use your model to get the best results..."
                value={description}
                onChange={(e) => {
                  if (e.target.value.length <= 200) {
                    setDescription(e.target.value);
                  }
                }}
                rows={4}
                maxLength={200}
              />
              <ThinText>
                {description.length}/200 characters - Guide users on how to use
                your model effectively
              </ThinText>
            </ModelColumn>
          ) : (
            <ModelColumn style={{ gap: "8px", flex: "unset" }}>
              <ModelTitleRow>
                <ModelDescription>Usage Instructions</ModelDescription>
                <EditIcon
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    setEditDescription(true);
                  }}
                />
              </ModelTitleRow>
              <Value
                style={{ opacity: 0.7, lineHeight: "1.5", fontSize: "14px" }}
              >
                {description ||
                  "No usage instructions provided yet. Click the edit icon to add instructions."}
              </Value>
            </ModelColumn>
          )}

          <ModelDescription>Select template tags</ModelDescription>

          <TagsContainer>
            {selectedTags.map((tag, index) => (
              <Tag key={index}>
                <TagText>{tag}</TagText>
              </Tag>
            ))}
            <EditIcon
              style={{ cursor: "pointer" }}
              onClick={() => {
                setShowEditTagsModal(true);
              }}
            />
          </TagsContainer>
        </ModelColumn>
        <Column style={{ gap: 9 }}>
          {user?.isVipCreator &&
            isVideoTraining &&
            !isBlendEnabled &&
            hasCoupleKeyword && (
              <Button
                style={{
                  width: 185,
                  border: "1px solid #ffffff",
                }}
                background={"transparent"}
                editTrainingPage
                textColor={"white"}
                title={"Enable Blend"}
                leftIcon={<UsersIcon />}
                loading={enableBlendLoading}
                onClick={handleEnableBlend}
              />
            )}
          {user?.isVipCreator && isVideoTraining && (
            <Button
              background={"transparent"}
              style={{
                width: 185,
                border: "1px solid #ffffff",
              }}
              editTrainingPage
              textColor={"white"}
              title={"Publish on Fapify"}
              leftIcon={<ShareIcon />}
              onClick={() => setIsPublishOnFapifyModalOpen(true)}
            />
          )}
          <Button
            leftIcon={<MagicStickSvg />}
            style={{
              width: 185,
            }}
            title={"Use Your Model"}
            onClick={handleGenerateAgain}
          />
          {isBlendEnabled && isBlendPublished && (
            <Button
              leftIcon={<MagicStickSvg />}
              style={{
                width: 185,
                opacity: !isBlendReadyToCreate || updateBlendLoading ? 0.6 : 1,
                cursor:
                  !isBlendReadyToCreate || updateBlendLoading
                    ? "not-allowed"
                    : "pointer",
              }}
              title={"Create Blend"}
              onClick={handleCreateBlendClick}
            />
          )}
        </Column>
      </ModelRow>

      {/* <LineHeader>
        <Line />
        <LineText>Videos</LineText>
        <Line />
      </LineHeader>
      <CollectionRow>
        {videoCollections.map((col, idx) => (
          <CollectionItem
            key={col._id}
            video={col}
            priority={idx < 4}
            onCollectionItemClick={() => {
              dispatch(
                setSelectedCollection({
                  ...col,
                  regenerate: false,
                  collectionId: col._id,
                }),
              );
              if (isImageOutput(col.modelType ?? col.modelName)) {
                setIsEditImageModalOpen(true);
              } else {
                setIsGenerateCollectionModalOpen(true);
              }
            }}
          />
        ))}
      </CollectionRow> */}

      {shouldShowBlendMode && (
        <>
          <ModelRow style={{ alignItems: "center", marginTop: 32 }}>
            <ModelTitle>Blend Mode</ModelTitle>
          </ModelRow>

          <ModelColumn style={{ marginTop: 16 }}>
            <InputContainer>
              <ModelDescription>Blend Template Name</ModelDescription>
              <FormInput
                placeholder="Enter blend template name"
                value={blendModelName}
                onChange={(e) => setBlendModelName(e.target.value)}
              />
            </InputContainer>

            <InputContainer>
              <ModelDescription>Blend Prompt</ModelDescription>
              <TextArea
                placeholder="Describe how the blend should group two photos"
                value={blendPrompt}
                onChange={(e) => setBlendPrompt(e.target.value)}
                rows={4}
              />
              <ThinText>
                This prompt will be used while creating group photo
              </ThinText>
              <Buttons>
                <Button
                  style={{
                    width: 185,
                  }}
                  title={"Save"}
                  onClick={handleSaveBlendSettings}
                  loading={updateBlendLoading}
                />
                <Button
                  leftIcon={<MagicStickSvg />}
                  style={{
                    width: 185,
                    opacity:
                      !isBlendReadyToCreate || updateBlendLoading ? 0.6 : 1,
                    cursor:
                      !isBlendReadyToCreate || updateBlendLoading
                        ? "not-allowed"
                        : "pointer",
                  }}
                  title={"Create Blend"}
                  onClick={handleCreateBlendClick}
                />
                {selectedCoverId && (
                  <Button
                    style={{ marginLeft: "auto" }}
                    title={"Publish"}
                    onClick={handlePublishBlend}
                    loading={publishBlendLoading}
                  />
                )}
              </Buttons>
            </InputContainer>
          </ModelColumn>
          {blendCollections?.length > 0 && (
            <>
              <LineHeader>
                <Line />
                <LineText>Videos</LineText>
                <Line />
              </LineHeader>

              <CollectionRow>
                {blendCollections.map((col, idx) => (
                  <CollectionItem
                    key={col._id}
                    video={col}
                    priority={idx < 4}
                    select
                    selectedCollections={
                      selectedCoverId ? [selectedCoverId] : []
                    }
                    setSelectedCollections={handleSelectCover}
                  />
                ))}
              </CollectionRow>
            </>
          )}
        </>
      )}

      <EditTagsModal
        isOpen={showEditTagsModal}
        onClose={() => setShowEditTagsModal(false)}
        onConfirm={(data) => {
          setSelectedTags(data);
          selectTagsAction({
            keywords: data,
            trainModelId: selectedTrainModel.data._id,
          });
        }}
        isVipCreator={user?.isVipCreator}
        tags={TAGS}
        selectedTags={selectedTags}
        setSelectedTags={setSelectedTags}
        isLoading={false}
      />
      <GenerateCollectionModal
        isOpen={isGenerateCollectionModalOpen}
        onClose={() => setIsGenerateCollectionModalOpen(false)}
      />

      <EditImageModal
        isOpen={isEditImageModalOpen}
        collection={selectedCollection}
        onClose={() => setIsEditImageModalOpen(false)}
      />

      {isVideoTraining && (
        <PublishOnFapifyModal
          isOpen={isPublishOnFapifyModalOpen}
          onClose={() => setIsPublishOnFapifyModalOpen(false)}
          trainModelId={selectedTrainModel?.data?._id}
        />
      )}

      <GroupModal
        isOpen={isGroupModalOpen}
        onClose={() => setIsGroupModalOpen(false)}
        fromTrainingSelectCoverPage
        modelId={selectedTrainModel?.data?.blendModel?._id}
      />
    </MainContainer>
  );
};

export default EditTrainingModelPage;
